/** @format */
import * as types from './actions';
import reducers from './reducers';
import request from '../../helpers/request';
import { enqueueNotification } from '../app';

export default reducers;

/**
 * fetches a dashboard of a portfolio company.
 * @param {UUID} portcoId Portfolio company'd id
 * @param {UUID} portcoId dashboard id that needs to be fetched
 * @returns Dashboard
 */
export const fetchPortfolioDashboard = (portcoId, dashboardId) => {
  return async dispatch => {
    if (!portcoId || !dashboardId) {
      const message = 'Something went wrong while fetching the dashboard.';
      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          dashboardId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }
    dispatch({
      type: types.LOAD_PORTFOLIO_DASHBOARD_REQUEST,
      payload: {
        portcoId,
        dashboardId,
      },
    });
    let data;
    try {
      const response = await request.get(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}?withComponents=true`,
        {},
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message = 'Something went wrong while loading the dashboard.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARD_SUCCESS,
        payload: {
          portcoId,
          dashboardId,
          dashboard: data.data,
        },
      });
      return data.data;
    } else {
      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * adds a new chart to the dashboard of a portfolio company.
 * @param {UUID} portcoId Portfolio company'd id
 * @param {UUID} dashboardId dashboard's id of a portfolio where the chart needs to be added
 * @param {Object} payload attributes to create a new chart
 * @returns New chart
 */
export const addChartToDashboard = (portcoId, dashboardId, payload) => {
  return async dispatch => {
    if (!portcoId || !dashboardId) {
      const message = 'Something went wrong while adding a chart.';
      dispatch({
        type: types.ADD_CHART_TO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }
    dispatch({
      type: types.ADD_CHART_TO_DASHBOARD_REQUEST,
      payload: {
        portcoId,
        dashboardId,
      },
    });
    let data;
    try {
      const response = await request.post(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}/charts`,
        payload,
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message =
        'Something went wrong while adding a new chart to the dashboard. Please try again.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.ADD_CHART_TO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      const chart = data.data;
      dispatch({
        type: types.ADD_CHART_TO_DASHBOARD_SUCCESS,
        payload: {
          portcoId,
          dashboardId,
          data: chart,
        },
      });
      dispatch(
        enqueueNotification('Chart added to the dashboard successfully!')
      );
      return chart;
    } else {
      dispatch({
        type: types.ADD_CHART_TO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * removes a chart from a  of a dashboard with id : dashboardId and will add the chart to the dashboard
 * with id : dashboard_id_where_to_move
 * @param {UUID} portcoId Portfolio company'd id
 * @param {UUID} portcoId dashboard id of a portfolio where the chart exist
 * @param {UUID} chartid chart id
 * @param {UUID} dashboard_id_where_to_move id of the dashboard where to move the chart
 * @returns
 */
export const moveChartToOtherDashboard = (
  portcoId,
  dashboardId,
  chartId,
  dashboard_id_where_to_move
) => {
  return async dispatch => {
    let data;
    const commonPayload = {
      portcoId,
      dashboardId,
      chartId,
      dashboard_id_where_to_move,
    };
    try {
      const response = await request.post(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}/charts/${chartId}/move`,
        { dashboard_id: dashboard_id_where_to_move },
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message =
        'Something went wrong while moving a chart to the other dashboard.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.MOVE_CHART_TO_DASHBOARD_FAILURE,
        payload: {
          ...commonPayload,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      dispatch({
        type: types.MOVE_CHART_TO_DASHBOARD_SUCCESS,
        payload: { ...commonPayload },
      });
      dispatch(enqueueNotification('Chart has been moved successfully!'));
      return null;
    } else {
      dispatch({
        type: types.MOVE_CHART_TO_DASHBOARD_FAILURE,
        payload: {
          ...commonPayload,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * updates a chart of a portfolio dashboard.
 * @param {UUID} portcoId Portfolio company'd id
 * @param {UUID} portcoId dashboard id of a portfolio where the chart exist
 * @param {UUID} chartid chart id
 * @param {Object} payload attributes to create a new chart
 * @returns updated chart
 */
export const updateDashboardChart = (
  portcoId,
  dashboardId,
  chartId,
  payload,
  { showSuccessMessage = true, successMessage, errorMessage }
) => {
  return async dispatch => {
    const commonPayload = {
      portcoId,
      dashboardId,
      chartId,
    };
    dispatch({
      type: types.UPDATE_DASHBOARD_CHART_REQUEST,
      payload: commonPayload,
    });
    let data;
    try {
      const response = await request.patch(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}/charts/${chartId}`,
        payload,
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message =
        errorMessage ||
        'Something went wrong while updating the chart. Please try again.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.UPDATE_DASHBOARD_CHART_FAILURE,
        payload: {
          ...commonPayload,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      const chart = data.data;
      dispatch({
        type: types.UPDATE_DASHBOARD_CHART_SUCCESS,
        payload: {
          ...commonPayload,
          data: chart,
        },
      });
      if (showSuccessMessage) {
        dispatch(
          enqueueNotification(successMessage || 'chart updated successfully!')
        );
      }
      return chart;
    } else {
      let message =
        data.message ||
        errorMessage ||
        'Something went wrong while updating the chart. Please try again.';
      dispatch({
        type: types.UPDATE_DASHBOARD_CHART_FAILURE,
        payload: {
          ...commonPayload,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }
  };
};

/**
 * deletes a chart from a portfolio dashboard.
 * @param {UUID} portcoId Portfolio company'd id
 * @param {UUID} portcoId dashboard id of a portfolio where the chart exist
 * @param {UUID} chartid chart id
 * @returns {null} null
 */
export const deleteChartFromDashboard = (
  portcoId,
  dashboardId,
  componentId,
  { isOlderChart = false, chartId }
) => {
  return async dispatch => {
    const commonPayload = {
      portcoId,
      dashboardId,
      componentId,
    };
    dispatch({
      type: types.DELETE_CHART_FROM_DASHBOARD_FAILURE,
      payload: commonPayload,
    });

    let data;
    try {
      const response = await request.del(
        !isOlderChart
          ? `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}/charts/${componentId}`
          : `/api/portfolio/v1/companies/${portcoId}/dashboards/${dashboardId}/components/${chartId}`,
        {},
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message =
        'Something went wrong while deleting the chart. Please try again.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.DELETE_CHART_FROM_DASHBOARD_FAILURE,
        payload: {
          ...commonPayload,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      dispatch({
        type: types.DELETE_CHART_FROM_DASHBOARD_SUCCESS,
        payload: commonPayload,
      });
      dispatch(enqueueNotification('chart deleted successfully!'));
      return null;
    } else {
      dispatch({
        type: types.DELETE_CHART_FROM_DASHBOARD_FAILURE,
        payload: {
          ...commonPayload,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * Adds a new dashboard to a portfolio company.
 * @param {UUID} portcoId Portfolio company id
 * @param {Object} payload attributes to create a new dashboard
 * @returns newly created dashboard
 */
export const addDashboardToPortfolio = (portcoId, payload) => {
  return async dispatch => {
    if (!portcoId) {
      const message = 'Something went wrong while adding a new dashboard.';
      dispatch({
        type: types.ADD_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }
    dispatch({
      type: types.ADD_PORTFOLIO_DASHBOARD_REQUEST,
      payload: {
        portcoId,
      },
    });
    let data;
    try {
      const response = await request.post(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards`,
        payload,
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message =
        'Something went wrong while adding a new dashboard to the portfolio company. Please try again.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.ADD_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      const dashboard = data.data;
      dispatch({
        type: types.ADD_PORTFOLIO_DASHBOARD_SUCCESS,
        payload: {
          portcoId,
          dashboard,
        },
      });
      dispatch(enqueueNotification('dashboard added successfully!'));
      return dashboard;
    } else {
      dispatch({
        type: types.ADD_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * adds a duplicated dashboard to the portfolio company.
 * @param {UUID} portcoId Portfolio company id
 * @param {UUID} dashboardId id of the dashboard
 * @returns {Object} duplicated dashboard
 */
export const duplicatePortfolioDashboard = (portcoId, dashboardId, options) => {
  return async dispatch => {
    if (!portcoId || !dashboardId) {
      const message = 'Something went wrong while duplicating a dashboard.';
      dispatch({
        type: types.DUPLICATE_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      throw new Error(message);
    }

    let data;
    try {
      const response = await request.post(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}/duplicate`,
        { is_note_included: options.is_note_included },
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message = 'Something went wrong while duplicating a dashboard.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      const duplicatedDashboard = data.data;
      dispatch({
        type: types.DUPLICATE_PORTFOLIO_DASHBOARD_SUCCESS,
        payload: {
          portcoId,
          dashboard: duplicatedDashboard,
        },
      });
      dispatch(enqueueNotification('Duplicated dashboard added successfully!'));
      return duplicatedDashboard;
    } else {
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * fetches all the dashboards of a portfolio company
 * @param {UUID} portcoId Portfolio company id
 * @returns {Array} list of dashboards in a portfolio
 */
export const loadPortfolioDashboards = portcoId => {
  return async dispatch => {
    if (!portcoId) {
      const message = 'Something went wrong while loading dashboards.';
      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }
    dispatch({
      type: types.LOAD_PORTFOLIO_DASHBOARDS_REQUEST,
      payload: {
        portcoId,
      },
    });
    let data;
    try {
      const response = await request.get(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards?withComponents=true`,
        {},
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message = 'Something went wrong while loading dashboards.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARDS_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      const dashboards = data.data;
      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARDS_SUCCESS,
        payload: {
          portcoId,
          dashboards,
        },
      });
      return dashboards;
    } else {
      dispatch({
        type: types.LOAD_PORTFOLIO_DASHBOARDS_FAILURE,
        payload: {
          portcoId,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * Updates the dashboard Layout.
 * @param {UUID} portcoId Portfolio company id
 * @param {UUID} dashboardId dashboard id
 * @param {Object} payload coordinates of charts
 * @returns
 */
export const saveDashboardLayout = (portcoId, dashboardId, payload) => {
  return async dispatch => {
    if (!portcoId || !dashboardId || !payload) {
      const message =
        'Something went wrong while updating the dashboard layout.';
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }

    let data;
    try {
      const response = await request.patch(
        `/api/portfolio/v1/companies/${portcoId}/dashboards/${dashboardId}`,
        payload,
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message = 'Something went wrong while updating the dashboard layout.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      dispatch({
        type: types.UPDATE_DASHBOARD_LAYOUT,
        payload: {
          portcoId,
          dashboardId,
          data: payload,
        },
      });
      return null;
    } else {
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * removes a dashboard from a portfolio company.
 * @param {UUID} portcoId Portfolio company id
 * @param {UUID} dashboardId dashboard id
 * @returns
 */

export const removeDashboardFromPortfolio = (portcoId, dashboardId) => {
  return async dispatch => {
    if (!portcoId) {
      const message = 'Something went wrong while deleting. Please try again';
      dispatch({
        type: types.REMOVE_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }
    dispatch({
      type: types.REMOVE_PORTFOLIO_DASHBOARD_REQUEST,
      payload: {
        portcoId,
      },
    });
    let data;
    try {
      const response = await request.del(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}`,
        {},
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message = 'Something went wrong while deleting. Please try again.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.REMOVE_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      dispatch({
        type: types.REMOVE_PORTFOLIO_DASHBOARD_SUCCESS,
        payload: {
          portcoId,
          dashboardId,
        },
      });
      dispatch(enqueueNotification('dashboard removed successfully!'));
      return null;
    } else {
      dispatch({
        type: types.REMOVE_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
    }
  };
};

/**
 * Updates the portfolio dashboard.
 * @param {UUID} portcoId Portfolio company id
 * @param {UUID} dashboardId dashboard id
 * @param {Object} payload attributes to be updated
 * @returns {Object} updated dashboard
 */
export const updatePortfolioDashboard = (portcoId, dashboardId, payload) => {
  return async dispatch => {
    if (!portcoId || !dashboardId) {
      const message = 'Something went wrong while updating the dashboard.';
      dispatch({
        type: types.UPDATE_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          dashboardId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw new Error(message);
    }
    dispatch({
      type: types.UPDATE_PORTFOLIO_DASHBOARD_REQUEST,
      payload: {
        portcoId,
        dashboardId,
      },
    });
    let data;
    try {
      const response = await request.patch(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}`,
        payload,
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message = 'Something went wrong while updating the dashboard.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch({
        type: types.UPDATE_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          dashboardId,
          message,
        },
      });
      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      const updatedDashboard = data.data;
      dispatch({
        type: types.UPDATE_PORTFOLIO_DASHBOARD_SUCCESS,
        payload: {
          portcoId,
          dashboardId,
          dashboard: updatedDashboard,
        },
      });
      dispatch(enqueueNotification('dashboard updated successfully!'));
      return updatedDashboard;
    } else {
      dispatch({
        type: types.UPDATE_PORTFOLIO_DASHBOARD_FAILURE,
        payload: {
          portcoId,
          dashboardId,
          message: data.message,
        },
      });
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * Converts the older portfolio chart into new cube js chart
 * @param {UUID} portcoId Portfolio company'd id
 * @param {UUID} portcoId dashboard id of a portfolio where the chart exist
 * @param {UUID} chartid chart id
 * @param {Object} payload attributes to create a new chart
 * @returns
 */
export const convertOlderChartToNewerChart = (
  portcoId,
  dashboardId,
  chartId,
  payload
) => {
  return async dispatch => {
    let data;
    const commonPayload = {
      portcoId,
      dashboardId,
      chartId,
    };
    try {
      await request.del(
        `/api/portfolio/v1/companies/${portcoId}/dashboards/${dashboardId}/components/${chartId}`,
        {},
        dispatch
      );
      const response = await request.post(
        `/api/portfolio/dashboards/v2/companies/${portcoId}/boards/${dashboardId}/charts`,
        payload,
        dispatch
      );
      data = response.data;
    } catch (err) {
      let message =
        'Something went wrong while converting the older chart to the newer chart.';
      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.message
      ) {
        message = err.response.data.message;
      }

      dispatch(enqueueNotification(message));
      throw err;
    }
    if (data.success) {
      dispatch({
        type: types.CONVERT_OLD_CHART_TO_NEW_CHART,
        payload: { ...commonPayload, chart: data.data },
      });
      dispatch(enqueueNotification('Chart has been converted successfully!'));
      return null;
    } else {
      dispatch(enqueueNotification(data.message));
      throw new Error(data.message);
    }
  };
};

/**
 * Sets or updates the recently shared users for a given dashboard.
 * @param {UUID} portcoId Portfolio company ID
 * @param {UUID} dashboardId Dashboard ID
 * @param {Array} people Array of people with at least `email_primary`
 * @returns null
 */
export const updateRecentlySharedPeople = (
  portcoId,
  dashboardId,
  people = []
) => {
  return async dispatch => {
    dispatch({
      type: types.UPDATE_RECENTLY_SHARED_PEOPLE,
      payload: {
        portcoId,
        dashboardId,
        people,
      },
    });
  };
};
